import type { ReactNode } from "react";
import { formatLanguages, languageListTitle, personaState, type PersonaVariant } from "../model/types";
import { PersonaStatusIcon } from "./PersonaStatusIcon";

// Only the legacy personas get a static label; the 18 per-role personas display
// their `role_name` from the available-variants endpoint, which takes priority.
const PERSONA_LABELS: Record<string, string> = {
  auto: "Auto",
  podcaster: "Podcaster",
  youtuber: "YouTuber",
  journalist: "Journalist",
  lecturer: "Lecturer",
  business: "Business",
  transcript: "Transcript",
};

interface PersonaChipProps {
  variant: PersonaVariant;
  isActive: boolean;
  /** Source/original language code, used to label the "" (source) variant. */
  sourceLanguage?: string;
  onClick: () => void;
  /** Rendered in place of the language badge — the strip passes a LanguageDropdown
   *  for the active, multi-language, fully generated chip. */
  trailing?: ReactNode;
}

/** A persona as a pill. Knows how to render a persona and its state; knows nothing
 *  about switching, adding or any other action. */
export function PersonaChip({ variant, isActive, sourceLanguage, onClick, trailing }: PersonaChipProps) {
  const state = personaState(variant);

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border pl-3 pr-1.5 py-1 transition-colors ${
        isActive ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"
      }`}
    >
      <button
        data-qa={`persona-chip-${variant.persona}`}
        onClick={onClick}
        className="text-sm font-medium inline-flex items-center gap-1.5"
      >
        <PersonaStatusIcon state={state} />
        {variant.role_name || PERSONA_LABELS[variant.persona] || variant.persona}
      </button>

      {state === "generating" ? (
        <span
          className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded ${
            isActive ? "bg-background/20 text-background" : "bg-muted text-muted-foreground"
          }`}
          title={`${variant.pending} report${variant.pending === 1 ? "" : "s"} still generating`}
        >
          {variant.pending} left
        </span>
      ) : (
        (trailing ?? (
          <span
            className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded ${
              isActive ? "bg-background/20 text-background" : "bg-muted text-muted-foreground"
            }`}
            title={languageListTitle(variant.languages, sourceLanguage)}
          >
            {formatLanguages(variant.languages, sourceLanguage)}
          </span>
        ))
      )}
    </div>
  );
}
